import { JsonProperty, Any, JsonObject } from 'json2typescript';
import { ModelObject } from '@hermes/api-model-core';
import { GridProduct } from '@hermes/api-model-product';
import { Edito, EditoResponse } from '@hermes/api-model-editorial';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Class Alternatives used in search case to map cde answer
 */
let Alternatives = class Alternatives extends ModelObject {
    constructor() {
        super();
        /** original asked term */
        this.originalSearch = undefined;
        /** result term */
        this.resultsFor = undefined;
        /** total number of items */
        this.total = 0;
    }
};
__decorate([
    JsonProperty("originalSearch", Any, true),
    __metadata("design:type", Object)
], Alternatives.prototype, "originalSearch", void 0);
__decorate([
    JsonProperty("resultsFor", Any, true),
    __metadata("design:type", Object)
], Alternatives.prototype, "resultsFor", void 0);
Alternatives = __decorate([
    JsonObject("Alternatives"),
    __metadata("design:paramtypes", [])
], Alternatives);

/**
 * This object is used to describe facet data.
 * Fields contains SEO and visuals data such as labels and paths.
 */
let Facet = class Facet extends ModelObject {
    constructor() {
        super(...arguments);
        this.title = undefined;
        /** Selected facet filter label translated */
        this.name = undefined;
        /** Current path with facet param */
        this.path = undefined;
        /** Alternative paths with the same facet param */
        this.altPaths = undefined;
        /** Meaning error */
        this.error = undefined;
    }
};
__decorate([
    JsonProperty("title", String, true),
    __metadata("design:type", String)
], Facet.prototype, "title", void 0);
__decorate([
    JsonProperty("name", String, true),
    __metadata("design:type", String)
], Facet.prototype, "name", void 0);
__decorate([
    JsonProperty("path", String, true),
    __metadata("design:type", String)
], Facet.prototype, "path", void 0);
__decorate([
    JsonProperty("altPaths", Object, true),
    __metadata("design:type", Object)
], Facet.prototype, "altPaths", void 0);
__decorate([
    JsonProperty("error", String, true),
    __metadata("design:type", String)
], Facet.prototype, "error", void 0);
Facet = __decorate([
    JsonObject("Facet")
], Facet);

/**
 * This class contains the filter parameters send by Fredhopper
 */
let Filter = class Filter extends ModelObject {
    constructor() {
        super();
        /** Name of the filter */
        this.name = undefined;
        this.selected = undefined;
        this.total = undefined;
        this.urlParams = undefined;
        this.value = undefined;
        this.facetIsCrawlable = undefined;
        this.facetUrlParameterName = undefined;
        this.combinedFilterImage = undefined;
        this.filterGroupName = undefined;
    }
};
__decorate([
    JsonProperty("name", String, true),
    __metadata("design:type", String)
], Filter.prototype, "name", void 0);
__decorate([
    JsonProperty("selected", Boolean, true),
    __metadata("design:type", Boolean)
], Filter.prototype, "selected", void 0);
__decorate([
    JsonProperty("total", Number, true),
    __metadata("design:type", Number)
], Filter.prototype, "total", void 0);
__decorate([
    JsonProperty("urlParams", String, true),
    __metadata("design:type", String)
], Filter.prototype, "urlParams", void 0);
__decorate([
    JsonProperty("value", String, true),
    __metadata("design:type", String)
], Filter.prototype, "value", void 0);
__decorate([
    JsonProperty("facetIsCrawlable", Boolean, true),
    __metadata("design:type", Boolean)
], Filter.prototype, "facetIsCrawlable", void 0);
__decorate([
    JsonProperty("facetUrlParameterName", String, true),
    __metadata("design:type", String)
], Filter.prototype, "facetUrlParameterName", void 0);
__decorate([
    JsonProperty("combinedFilterImage", String, true),
    __metadata("design:type", String)
], Filter.prototype, "combinedFilterImage", void 0);
Filter = __decorate([
    JsonObject("Filter"),
    __metadata("design:paramtypes", [])
], Filter);

/**
 * This class contains the filter group parameters send by Fredhopper
 */
let FilterGroup = class FilterGroup extends ModelObject {
    constructor() {
        super();
        /** Name of the group */
        this.name = undefined;
        /** Attribute of the filter */
        this.attribute = undefined;
        /** Hint for the screenreader mode */
        this.displayHint = undefined;
        /** Presentation of the filter */
        this.presentation = undefined;
        /** Indicates if the filter group can be used as a Grid Highlight */
        this.isGridHighlight = undefined;
        this.facetUrlParameterName = undefined;
        this.isOpen = undefined;
        this.isFirstRender = undefined;
        /** collection of filters */
        this.filters = [];
    }
};
__decorate([
    JsonProperty("name", String, true),
    __metadata("design:type", String)
], FilterGroup.prototype, "name", void 0);
__decorate([
    JsonProperty("attribute", String, true),
    __metadata("design:type", String)
], FilterGroup.prototype, "attribute", void 0);
__decorate([
    JsonProperty("displayHint", String, true),
    __metadata("design:type", String)
], FilterGroup.prototype, "displayHint", void 0);
__decorate([
    JsonProperty("presentation", String, true),
    __metadata("design:type", String)
], FilterGroup.prototype, "presentation", void 0);
__decorate([
    JsonProperty("isGridHighlight", Boolean, true),
    __metadata("design:type", Boolean)
], FilterGroup.prototype, "isGridHighlight", void 0);
__decorate([
    JsonProperty("facetUrlParameterName", String, true),
    __metadata("design:type", String)
], FilterGroup.prototype, "facetUrlParameterName", void 0);
__decorate([
    JsonProperty("isOpen", Boolean, true),
    __metadata("design:type", Boolean)
], FilterGroup.prototype, "isOpen", void 0);
__decorate([
    JsonProperty("isFirstRender", Boolean, true),
    __metadata("design:type", Boolean)
], FilterGroup.prototype, "isFirstRender", void 0);
__decorate([
    JsonProperty("filters", [Filter], true),
    __metadata("design:type", Array)
], FilterGroup.prototype, "filters", void 0);
FilterGroup = __decorate([
    JsonObject("FilterGroup"),
    __metadata("design:paramtypes", [])
], FilterGroup);

/**
 * This interface is used to as value for ProductCollectionResponse
 */
let ProductCollection = class ProductCollection extends ModelObject {
    constructor() {
        super(...arguments);
        /** Array of Product */
        this.items = [];
        /** Maxmum size of array */
        this.maxSize = undefined;
    }
};
__decorate([
    JsonProperty("items", [GridProduct], true),
    __metadata("design:type", Array)
], ProductCollection.prototype, "items", void 0);
__decorate([
    JsonProperty("maxSize", Number, true),
    __metadata("design:type", Number)
], ProductCollection.prototype, "maxSize", void 0);
ProductCollection = __decorate([
    JsonObject("ProductCollection")
], ProductCollection);

/**
 * This interface is used to describe data in response of GridProduct[]
 */
let ProductCollectionResponse = class ProductCollectionResponse extends ModelObject {
    constructor() {
        super(...arguments);
        /** Total items */
        this.total = undefined;
        /** Product Collection */
        this.productCollection = undefined;
        /** Array of FilterGroup */
        this.filters = [];
        /** Array of Edito */
        this.editos = [];
        /** Array of EditoResponse */
        this.editorials = [];
        /** Allow to define if is an alternative search result */
        this.alternatives = new Alternatives();
        /** redirectionUrl */
        this.redirectionUrl = "";
        /** Allow to define if is an alternative search result */
        this.url = "";
        this.facet = undefined;
        /** Indicates if the grid is hero product */
        this.isHeroProduct = false;
    }
};
__decorate([
    JsonProperty("total", Number, true),
    __metadata("design:type", Number)
], ProductCollectionResponse.prototype, "total", void 0);
__decorate([
    JsonProperty("products", ProductCollection, true),
    __metadata("design:type", ProductCollection)
], ProductCollectionResponse.prototype, "productCollection", void 0);
__decorate([
    JsonProperty("filtersGroup", [FilterGroup]),
    __metadata("design:type", Array)
], ProductCollectionResponse.prototype, "filters", void 0);
__decorate([
    JsonProperty("editos", [Edito], true),
    __metadata("design:type", Array)
], ProductCollectionResponse.prototype, "editos", void 0);
__decorate([
    JsonProperty("editorials", [EditoResponse], true),
    __metadata("design:type", Array)
], ProductCollectionResponse.prototype, "editorials", void 0);
__decorate([
    JsonProperty("alternatives", Alternatives, true),
    __metadata("design:type", Alternatives)
], ProductCollectionResponse.prototype, "alternatives", void 0);
__decorate([
    JsonProperty("redirectionUrl", String, true),
    __metadata("design:type", String)
], ProductCollectionResponse.prototype, "redirectionUrl", void 0);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], ProductCollectionResponse.prototype, "url", void 0);
__decorate([
    JsonProperty("facet", Facet, true),
    __metadata("design:type", Facet)
], ProductCollectionResponse.prototype, "facet", void 0);
__decorate([
    JsonProperty("isHeroProduct", Boolean, true),
    __metadata("design:type", Boolean)
], ProductCollectionResponse.prototype, "isHeroProduct", void 0);
ProductCollectionResponse = __decorate([
    JsonObject("ProductCollectionResponse")
], ProductCollectionResponse);

export { Alternatives, Facet, Filter, FilterGroup, ProductCollection, ProductCollectionResponse };
